/**
 * Account Tray self-service page on `app.<base>` (ADR-0077 S2).
 *
 * Guarded by the identity session token (no token → bounce to the login at `/`).
 * Profile (email read-only — it's the identity KEY), password change (rotating the
 * session token so the page stays signed in), 2FA over the shared `TwoFactorPanel`
 * (identity adapter), and the ORGANIZATIONS the caller belongs to with their
 * workspaces and one-click handoff (ADR-0078 slice 3).
 */
import * as React from "react";
import { useTranslation } from "react-i18next";

import { TwoFactorPanel, type TotpAdapter } from "@/components/auth/totp";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { orgBillingLine, orgBillingSummary } from "@/lib/orgBilling";
import {
  identityApi,
  identityToken,
  onboardingApi,
  setIdentityToken,
  type IdentityMe,
  type IdentityOrganizations,
  type IdentityOrgWorkspace,
  type OnboardingPlan,
} from "@/lib/api-onboarding";

import { BuyWorkspaceDialog } from "./BuyWorkspaceDialog";

/** The shared 2FA panel's transport, over the identity endpoints. `status` derives
 * from `/me` (no dedicated status route); confirm returns no session token here. */
function identityTotpAdapter(me: () => Promise<IdentityMe>): TotpAdapter {
  return {
    status: async () => {
      const m = await me();
      return { enabled: m.totp_enabled, recovery_codes_remaining: 0 };
    },
    begin: () => identityApi.totpBegin(),
    confirm: async (code: string) => {
      const res = await identityApi.totpConfirm(code);
      return { recovery_codes: res.recovery_codes };
    },
    disable: async (code: string) => {
      await identityApi.totpDisable(code);
    },
  };
}

export function IdentityAccountPage() {
  const { t } = useTranslation();
  const [me, setMe] = React.useState<IdentityMe | null>(null);
  const [orgs, setOrgs] = React.useState<IdentityOrganizations | null>(null);
  const [newOrgName, setNewOrgName] = React.useState("");
  const [wsFor, setWsFor] = React.useState<string | null>(null);
  // The catalog the buyer picks from. Adding a workspace is a PURCHASE (ADR-0079), so
  // there is no "no plan" option — an empty catalog means nothing is on sale and the
  // form says so instead of offering a button that would 422.
  const [plans, setPlans] = React.useState<OnboardingPlan[] | null>(null);
  const [fullName, setFullName] = React.useState("");
  const [savedProfile, setSavedProfile] = React.useState(false);
  const [currentPw, setCurrentPw] = React.useState("");
  const [newPw, setNewPw] = React.useState("");
  const [pwNotice, setPwNotice] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const bootedRef = React.useRef(false);
  React.useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    if (!identityToken()) {
      window.location.replace("/");
      return;
    }
    identityApi
      .me()
      .then((m) => {
        setMe(m);
        setFullName(m.full_name ?? "");
      })
      .catch(() => {
        setIdentityToken(null);
        window.location.replace("/");
      });
    // Best-effort: the org view is a richer READING of the same arcs, so a hiccup here
    // must not cost the page its profile/password/2FA — the flat workspace list below
    // is the fallback render.
    identityApi
      .organizations()
      .then(setOrgs)
      .catch(() => setOrgs(null));
  }, []);

  const adapter = React.useMemo(() => identityTotpAdapter(() => identityApi.me()), []);

  async function saveProfile(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSavedProfile(false);
    setBusy(true);
    try {
      const m = await identityApi.patchMe({ full_name: fullName.trim() || null });
      setMe(m);
      setSavedProfile(true);
    } catch {
      setError(t("identityAccount.saveFailed", "Salvataggio non riuscito."));
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setPwNotice(null);
    setBusy(true);
    try {
      const res = await identityApi.changePassword({
        current_password: currentPw,
        new_password: newPw,
      });
      // The change bumps token_version (kills every session) — adopt the fresh one.
      setIdentityToken(res.token);
      setCurrentPw("");
      setNewPw("");
      setPwNotice(t("identityAccount.pwChanged", "Password aggiornata."));
    } catch {
      setError(
        t(
          "identityAccount.pwFailed",
          "Cambio password non riuscito. Controlla la password attuale.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  async function enter(slug: string) {
    try {
      const res = await identityApi.handoff(slug);
      window.location.href = res.redirect_url;
    } catch {
      setError(t("identityAccount.handoffFailed", "Accesso al workspace non riuscito."));
    }
  }

  async function reloadOrgs() {
    setOrgs(await identityApi.organizations());
  }

  async function createOrg(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await identityApi.createOrganization(newOrgName.trim());
      setNewOrgName("");
      await reloadOrgs();
    } catch {
      setError(t("identityAccount.orgCreateFailed", "Creazione dell'organizzazione non riuscita."));
    } finally {
      setBusy(false);
    }
  }

  function signOut() {
    setIdentityToken(null);
    window.location.replace("/");
  }

  if (!me) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <p className="text-sm text-muted-foreground">
          {t("identityAccount.loading", "Caricamento…")}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto w-full max-w-2xl space-y-6 py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/tray-logo.svg" alt="" aria-hidden="true" className="h-7 w-7 shrink-0" />
            <span className="text-base font-semibold tracking-tight">Account Tray</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <a href="/" className="text-muted-foreground underline-offset-2 hover:underline">
              {t("identityAccount.toLogin", "Workspace")}
            </a>
            <button
              type="button"
              onClick={signOut}
              className="text-muted-foreground underline-offset-2 hover:underline"
            >
              {t("identityAccount.signOut", "Esci")}
            </button>
          </div>
        </div>

        {error && (
          <Alert tone="danger" title={t("identityAccount.errorTitle", "Errore")}>
            {error}
          </Alert>
        )}

        <section className="space-y-4 rounded-lg border border-border/60 bg-surface p-5">
          <h2 className="text-sm font-medium">{t("identityAccount.profile", "Profilo")}</h2>
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="acc-email">{t("identityAccount.email", "Email")}</Label>
              <Input id="acc-email" value={me.email} disabled />
              <p className="text-xs text-muted-foreground">
                {t(
                  "identityAccount.emailHint",
                  "L'email è la chiave del tuo account e non è modificabile da qui.",
                )}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-name">{t("identityAccount.fullName", "Nome completo")}</Label>
              <Input id="acc-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" size="sm" disabled={busy}>
                {t("identityAccount.save", "Salva")}
              </Button>
              {savedProfile && (
                <span className="text-sm text-success">
                  {t("identityAccount.saved", "Salvato.")}
                </span>
              )}
            </div>
          </form>
        </section>

        <section className="space-y-4 rounded-lg border border-border/60 bg-surface p-5">
          <h2 className="text-sm font-medium">{t("identityAccount.password", "Password")}</h2>
          <form onSubmit={changePassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="acc-pw-current">
                {t("identityAccount.currentPw", "Password attuale")}
              </Label>
              <Input
                id="acc-pw-current"
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-pw-new">{t("identityAccount.newPw", "Nuova password")}</Label>
              <Input
                id="acc-pw-new"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            {pwNotice && <Alert tone="info">{pwNotice}</Alert>}
            <Button type="submit" size="sm" disabled={busy}>
              {t("identityAccount.changePw", "Cambia password")}
            </Button>
          </form>
        </section>

        <section className="space-y-4 rounded-lg border border-border/60 bg-surface p-5">
          <h2 className="text-sm font-medium">
            {t("identityAccount.twoFactor", "Autenticazione a due fattori")}
          </h2>
          <TwoFactorPanel adapter={adapter} />
        </section>

        {orgs && (
          <section className="space-y-3 rounded-lg border border-border/60 bg-surface p-5">
            <h2 className="text-sm font-medium">
              {t("identityAccount.organizations", "Le tue organizzazioni")}
            </h2>
            {orgs.organizations.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t(
                  "identityAccount.noOrganizations",
                  "Non appartieni a nessuna organizzazione. Creane una per avere un tuo spazio.",
                )}
              </p>
            )}
            <div className="space-y-4">
              {orgs.organizations.map((org) => (
                <div key={org.id} className="rounded-md border border-border/60">
                  <div className="flex items-center justify-between border-b border-border/60 px-4 py-2.5">
                    <span className="text-sm font-medium">{org.name}</span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {org.role === "admin"
                        ? t("identityAccount.roleAdmin", "Amministratore")
                        : t("identityAccount.roleMember", "Membro")}
                    </span>
                  </div>
                  {/* The billing picture, DERIVED — no new endpoint and no cross-workspace
                      invoice: ADR-0076 settled that billing stays per deployment. */}
                  <div className="border-b border-border/60 px-4 py-1.5 text-xs text-muted-foreground">
                    {orgBillingLine(orgBillingSummary(org.workspaces)) ??
                      t("identityAccount.orgNoWorkspacesYet", "Nessun workspace ancora.")}
                  </div>
                  {org.workspaces.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-muted-foreground">
                      {t("identityAccount.orgNoWorkspaces", "Nessun workspace.")}
                    </p>
                  ) : (
                    <div className="divide-y divide-border/60">
                      {org.workspaces.map((w) => (
                        <WorkspaceRow key={w.slug} w={w} onEnter={enter} t={t} />
                      ))}
                    </div>
                  )}
                  {/* Buying a workspace is an ORG power, so the button is only offered
                      where the arc grants it — an admin. */}
                  {org.role === "admin" && (
                    <div className="border-t border-border/60 px-4 py-2.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setWsFor(org.id);
                          // Lazy: the catalog is only needed once someone is buying.
                          if (plans === null) {
                            void onboardingApi
                              .plans()
                              .then(setPlans)
                              .catch(() => setPlans([]));
                          }
                        }}
                      >
                        {t("identityAccount.addWorkspace", "Aggiungi workspace")}
                      </Button>
                      <BuyWorkspaceDialog
                        organizationId={org.id}
                        organizationName={org.name}
                        plans={plans}
                        open={wsFor === org.id}
                        onOpenChange={(open: boolean) => setWsFor(open ? org.id : null)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <form className="flex items-end gap-2 pt-1" onSubmit={(e) => void createOrg(e)}>
              <div className="flex-1 space-y-1">
                <Label htmlFor="new-org">
                  {t("identityAccount.newOrgName", "Nuova organizzazione")}
                </Label>
                <Input
                  id="new-org"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder={t("identityAccount.newOrgPlaceholder", "Nome dell'azienda")}
                />
              </div>
              <Button type="submit" variant="outline" disabled={busy || !newOrgName.trim()}>
                {t("identityAccount.createOrg", "Crea organizzazione")}
              </Button>
            </form>
          </section>
        )}

        <section className="space-y-3 rounded-lg border border-border/60 bg-surface p-5">
          <h2 className="text-sm font-medium">
            {orgs && orgs.organizations.length > 0
              ? t("identityAccount.otherWorkspaces", "Altri workspace")
              : t("identityAccount.workspaces", "I tuoi workspace")}
          </h2>
          {(() => {
            // With the org view loaded, THIS section is the guest case — workspaces under
            // no organization of mine (the consultant). Without it we degrade to the flat
            // membership list `/me` already carries.
            const rows: IdentityOrgWorkspace[] = orgs
              ? orgs.other_workspaces
              : me.workspaces.map((w) => ({
                  tenant_id: w.slug,
                  slug: w.slug,
                  name: w.name,
                  status: "active",
                  can_enter: true,
                }));
            if (rows.length === 0) {
              return orgs && orgs.organizations.length > 0 ? null : (
                <p className="text-sm text-muted-foreground">
                  {t(
                    "identityAccount.noWorkspaces",
                    "Nessun workspace collegato. Chiedi un invito a un amministratore.",
                  )}
                </p>
              );
            }
            return (
              <div className="space-y-2">
                {rows.map((w) => (
                  <div key={w.slug} className="rounded-md border border-border/60">
                    <WorkspaceRow w={w} onEnter={enter} t={t} />
                  </div>
                ))}
              </div>
            );
          })()}
        </section>
      </div>
    </div>
  );
}

/** One workspace line: name, slug, plan, and the door — or an honest note when the
 * caller administers it without being a member of it. */
function WorkspaceRow({
  w,
  onEnter,
  t,
}: {
  w: IdentityOrgWorkspace;
  onEnter: (slug: string) => void;
  t: (key: string, fallback: string) => string;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span>
        <span className="block text-sm font-medium">{w.name}</span>
        <span className="block text-xs text-muted-foreground">
          {w.slug}
          {w.plan_name ? ` · ${w.plan_name}` : ""}
        </span>
      </span>
      {w.can_enter ? (
        <Button size="sm" variant="outline" onClick={() => void onEnter(w.slug)}>
          {t("identityAccount.open", "Apri")}
        </Button>
      ) : (
        <span className="text-xs text-muted-foreground">
          {t("identityAccount.notAMember", "Non sei membro")}
        </span>
      )}
    </div>
  );
}
